import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {teamGroupToLabel} from '@/features/treatment-requests/teams/utils'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import React, {useMemo, useState} from 'react'
import {ProjectExtended} from '../../types'
import {useTheme} from 'styled-components'

export interface TeamBodyProps {
    team: ProjectExtended['team']
}

const ITEMS_PER_PAGE = 4

export const RegularTeamBody: React.FC<TeamBodyProps> = ({team}) => {
    const {t} = useTranslation()
    const [page, setPage] = useState(0)

    const filteredTeam = team.filter(member => !member.isAReserve)

    const referringDoctor = useMemo(
        () => filteredTeam.find(member => member.group === 'referring_doctor') || null,
        [filteredTeam]
    )
    const therapist = useMemo(() => filteredTeam.find(member => member.group === 'therapist') || null, [filteredTeam])

    // Remove referringDoctor and therapist from the team
    const excludedForSpecialRoles = [referringDoctor?.id, therapist?.id].filter(Boolean)
    const remainingTeamMembers = filteredTeam.filter(member => !excludedForSpecialRoles.includes(member.id))

    // Get the first two other members
    const firstTwoOtherMembers = remainingTeamMembers.slice(0, 2)

    // Create the first page with 4 elements: referringDoctor, therapist, and the first two other members
    const remapFirstPageteam = [referringDoctor, therapist, ...firstTwoOtherMembers].filter(
        item => !!item || item === null
    )

    // Remove the first two members already used in the first page from the other members
    const otherTeamMembers = remainingTeamMembers.slice(2)
    const paginatedTeam = otherTeamMembers.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
    const totalPages = Math.ceil(otherTeamMembers.length / ITEMS_PER_PAGE)

    const handleNextPage = () => {
        totalPages > page && setPage(page + 1)
    }

    const handlePreviousPage = () => {
        page > 0 && setPage(page - 1)
    }

    const {palette} = useTheme()

    return (
        <Flexbox direction="column" gap={4} fullWidth style={{height: '100%', justifyContent: 'space-between'}}>
            <Grid columns={2} gap={3} fullWidth>
                {page == 0 ? (
                    <>
                        {remapFirstPageteam.map((teamMember, index) => {
                            // For the first two slots (referringDoctor and therapist), handle the undefined case
                            if (index < 2) {
                                const groupKey = index === 0 ? 'referring_doctor' : 'therapist'
                                return teamMember ? (
                                    <DetailsField fullWidth key={`${groupKey}-${teamMember.id}`}>
                                        <Flexbox gap={1}>
                                            <Text palette="neutral" shade="600" size="textSm">
                                                {t(teamGroupToLabel[groupKey])}
                                            </Text>
                                            <Text palette="neutral" shade="500" size="textSm">
                                                {teamMember.specializationName}
                                            </Text>
                                        </Flexbox>
                                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                                            {teamMember.fullName}
                                        </Text>
                                    </DetailsField>
                                ) : (
                                    <DetailsField fullWidth key={`${groupKey}-empty`}>
                                        <Text palette="neutral" shade="600" size="textSm">
                                            {t(teamGroupToLabel[groupKey])}
                                        </Text>
                                        <Flexbox gap={1} fullWidth align="center">
                                            <AlertCircleIcon size={15} fill={palette.warning[500]} />
                                            <Text palette="warning" shade="800" size="textSm">
                                                {t('projects:team_member_to_compile')}
                                            </Text>
                                        </Flexbox>
                                    </DetailsField>
                                )
                            }

                            // For the other team members
                            return teamMember ? (
                                <DetailsField fullWidth key={teamMember.id}>
                                    <Flexbox gap={1}>
                                        <Text palette="neutral" shade="600" size="textSm">
                                            {t(teamGroupToLabel[teamMember.group])}
                                        </Text>
                                        <Text palette="neutral" shade="500" size="textSm">
                                            {teamMember.specializationName}
                                        </Text>
                                    </Flexbox>
                                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                                        {teamMember.fullName}
                                    </Text>
                                </DetailsField>
                            ) : null
                        })}
                    </>
                ) : (
                    paginatedTeam.map(teamMember => (
                        <DetailsField fullWidth key={teamMember.id}>
                            <Flexbox gap={1}>
                                <Text palette="neutral" shade="600" size="textSm">
                                    {t(teamGroupToLabel[teamMember.group])}
                                </Text>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {teamMember.specializationName}
                                </Text>
                            </Flexbox>
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {teamMember.fullName}
                            </Text>
                        </DetailsField>
                    ))
                )}
            </Grid>
            <Flexbox gap={2} justify="end" fullWidth>
                <Button variant="ghost" size="sm" shape="square" onClick={handlePreviousPage} disabled={page == 0}>
                    <ChevronLeftIcon />
                </Button>
                <Button variant="ghost" size="sm" shape="square" onClick={handleNextPage} disabled={totalPages == page}>
                    <ChevronRightIcon />
                </Button>
            </Flexbox>
        </Flexbox>
    )
}
